import { ReactNode, forwardRef } from "react";
import { Box, Button, ButtonProps, Tooltip } from "@mui/joy";

export interface QuietProps extends Omit<
    ButtonProps,
    "startDecorator" | "endDecorator" | "variant" | "color" | "size"
> {
    /** The glyph; a quiet control may be a label only when it carries state. */
    icon?: ReactNode;
    /** Shown as a badge; the control goes dark while it is non-zero. */
    count?: number;
    /** Tooltip for a glyph-only control. */
    title?: string;
    /** Where a quiet link goes (with `component="a"`): opening a page changes no data. */
    href?: string;
    target?: string;
    rel?: string;
}

/**
 * Quiet control: adjusts the view and changes no data. Plain text or glyph, no
 * chrome, hover tint only. A label only where the control carries state.
 * A real Joy Button underneath, so it can be a MenuButton root.
 */
export const Quiet = forwardRef<HTMLButtonElement, QuietProps>(function Quiet(
    { icon, count, title, children, sx, ...rest },
    ref,
) {
    const active = !!count;
    const button = (
        <Button
            ref={ref}
            variant="plain"
            color="neutral"
            data-level="quiet"
            startDecorator={icon}
            endDecorator={
                active ? (
                    <Box
                        sx={{
                            minWidth: 18,
                            height: 18,
                            px: 0.5,
                            borderRadius: 9,
                            bgcolor: "text.primary",
                            color: "background.surface",
                            fontSize: "var(--type-caption)",
                            fontWeight: 600,
                            display: "grid",
                            placeItems: "center",
                        }}
                        data-testid="quiet-count"
                    >
                        {count}
                    </Box>
                ) : undefined
            }
            {...rest}
            sx={[
                {
                    minHeight: 30,
                    px: children ? 1 : 0.75,
                    fontSize: "var(--type-secondary)",
                    fontWeight: active ? 600 : 500,
                    color: active ? "text.primary" : "text.secondary",
                    "--Button-gap": "6px",
                    "& svg": { fontSize: 18 },
                    "&:hover": { bgcolor: "background.level1", color: "text.primary" },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {children}
        </Button>
    );
    return children || !title ? (
        button
    ) : (
        <Tooltip title={title} variant="soft">
            {button}
        </Tooltip>
    );
});
